import { Settings } from "../../lib/timer";
import { StateInitializer } from "./stateinitializer";

type Id = string;

export interface State {
	version: number;
	timestamp: string;
}

export interface GlobalState extends State {
	timer: Settings;
	settings: {
		fontsize: number;
		randomize: boolean;
	};
}

export interface AttemptState extends State {
	current: {
		id?: Id;
		order: Record<Id, number>;
	}
	times: Record<Id, number>;
}

export type DeepPartial<T> = {
	[P in keyof T]?: T[P] extends Array<infer I>
		? Array<DeepPartial<I>>
		: DeepPartial<T[P]>
};

export function deepMerge<S = State>(target: S, source: DeepPartial<S>): S {
	function isObject(item: any): item is object {
		return item && typeof item === 'object' && !Array.isArray(item);
	}
	const output: S = Object.assign({}, target);
	for (let key of Object.keys(source)) {
		if (isObject(source[key])) {
			output[key] = deepMerge(target[key], source[key]);
		} else {
			Object.assign(output, { [key]: source[key] });
		}
	}
	return output;
}

export abstract class StateDataSource<S extends State> {
	constructor(initializer: StateInitializer<S>) {
		this.initializer = initializer;
	}

	initializer: StateInitializer<S>;

	abstract getStateKey(): string

	getInitialState(config: Settings): Promise<S> {
		try {
			return this.getState()
				.catch(e => undefined)
				.then((cache: S | undefined) =>
					this.initializer.getInitialState(cache, config)
				);
		} catch (e) {
			return Promise.reject(e);
		}
	}

	getState(): Promise<S> {
		try {
			const state: S = JSON.parse(localStorage.getItem(this.getStateKey()) ?? "null");
			return state ?
				Promise.resolve(state) :
				Promise.reject({
					reason: 'Missing/corrupt cache',
					key: this.getStateKey()
				});
		} catch (e) {
			return Promise.reject(e);
		}
	}

	setState(state: Omit<S, 'timestamp'> & Partial<Pick<S, 'timestamp'>>): Promise<void> {
		try {
			localStorage.setItem(this.getStateKey(), JSON.stringify({
				...state,
				timestamp: new Date().toISOString()
			}));
			return Promise.resolve();
		} catch (e) {
			return Promise.reject(e);
		}
	}

	updateState(state: DeepPartial<S>): Promise<void> {
		return this.getState().then(current_state =>
			this.setState(deepMerge(current_state, state))
		);
	}
}

export class NullStateDataSource<S extends State> extends StateDataSource<S> {
	getStateKey(): string {
		throw new Error("Method not implemented.");
	}
}